import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, 
  FileSpreadsheet, 
  File, 
  Search, 
  Trash2, 
  Eye,
  MessageSquare,
  Upload
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = "";

const FilesPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/files`, {
        withCredentials: true
      });
      setFiles(response.data);
    } catch (e) {
      console.error('Error fetching files:', e);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') return <FileText className="w-5 h-5 text-red-500" />;
    if (['xlsx', 'xls'].includes(fileType)) return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    if (fileType === 'csv') return <FileSpreadsheet className="w-5 h-5 text-blue-600" />;
    return <File className="w-5 h-5 text-zinc-500" />;
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/files/${fileToDelete.id}`, {
        withCredentials: true
      });
      setFiles(files.filter(f => f.id !== fileToDelete.id));
      toast.success('File deleted successfully');
    } catch (e) {
      toast.error('Failed to delete file');
    } finally {
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  const filteredFiles = files.filter(file => 
    file.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="files-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Files</h1>
          <p className="text-zinc-500 mt-1">Manage your uploaded data files</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => navigate('/upload')}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="upload-new-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload New
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="border-zinc-200 bg-white mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-zinc-200"
              data-testid="search-files-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Files Grid */}
      {filteredFiles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFiles.map((file) => (
            <Card 
              key={file.id} 
              className="border-zinc-200 bg-white hover:shadow-sm transition-shadow"
              data-testid={`file-card-${file.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-zinc-100 rounded-lg">
                    {getFileIcon(file.file_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-zinc-950 truncate" title={file.filename}>
                      {file.filename}
                    </h3>
                    <p className="text-xs text-zinc-500 mt-1">
                      {file.row_count?.toLocaleString() || 0} rows • {file.columns?.length || 0} columns
                    </p>
                    <p className="text-xs text-zinc-400 mt-1">
                      {file.created_at ? format(new Date(file.created_at), 'MMM d, yyyy HH:mm') : '-'}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/files/${file.id}`)}
                    className="flex-1 border-zinc-200"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/chat?file=${file.id}`)}
                    className="flex-1 border-zinc-200"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Chat
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFileToDelete(file);
                        setDeleteDialogOpen(true);
                      }}
                      className="border-zinc-200 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-zinc-200 bg-white">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-zinc-950 mb-2">No files found</h2>
            <p className="text-zinc-500 mb-4">
              {searchQuery ? 'Try a different search term' : 'Upload your first file to get started'}
            </p>
            {isAdmin && !searchQuery && (
              <Button
                onClick={() => navigate('/upload')}
                className="bg-blue-700 hover:bg-blue-800 text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload File
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{fileToDelete?.filename}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FilesPage;
